// Edit layer style > Rule > Geometry > Color Scheme > Classified

import { Box, Button, IconButton, MenuItem, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { theme_limeGreen, theme_orange, theme_textColorBlended, theme_textColorMain } from '../../../Theme';
import { ILayerStyleRule, ILayerStyleRule_Geometry, ILayerStyleType_Classified, ILayerStyleType_ClassifiedItem, ILayerStyleType_Gradient, ILayerStyleType_GradientItem } from './LayerStyleInterfaces';
import { CustomSelect, MuiColorInputStyled } from '../EditLayerStyle';
import { ChangeEvent, ReactNode, useState } from 'react';
import { FriendlyNumber, InterpolateColors } from '../../../Globals';
import { MuiColorInputColors } from 'mui-color-input';
import Debug from '../../../Debug';
import BigNumber from "bignumber.js";
import { CallServer } from '../../../CallServer';
import { ToastNotification } from '../../../ToastNotifications';
import { GetGradientPreset, ColorGradientPresets, GRADIENT_PRESET_EMPTY_ID } from '../../../Aois/AoiGroupAndPortfolioMap/ColorSchemeEditor/ColorGradientPresets';
import { CustomTextField } from '../EditLayer';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { GetVectorLayerAttributeByID } from '../../../AttributeOps';
import { ILayer } from '../../../Layers/LayerInterfaces';
import iwanthue from 'iwanthue';
import { RULE_EXPANSION_MAX_ITEMS } from './EditLayerStyle_ColorScheme_UniqueValues';
import { ILayerAttribExpression } from './LayerAttribExpressionInterfaces';

const MIN_CLASSES = 2;
const MAX_CLASSES = 20;

export const LAYER_STYLE_CLASSIFIED_DEFAULT_CLASS_COUNT = 4;
export const LAYER_STYLE_CLASSIFIED_DEFAULT_PRESET_ID = 3; // Yellow-Orange-Red (Continuous)



//-------------------------------------------------------------------------------
// Component props
//-------------------------------------------------------------------------------
export interface EditLayerStyle_ColorScheme_Classified_Props
{
  layer?: ILayer;
  rule: ILayerStyleRule;   // input
  OnRuleClassifiedUpdated: any;      // output
  OnExpandRule: any;
}

//-------------------------------------------------------------------------------
// Edit layer style > Rule > Geometry > Color Scheme > Classified
//-------------------------------------------------------------------------------
export function EditLayerStyle_ColorScheme_Classified(props: EditLayerStyle_ColorScheme_Classified_Props) 
{



  // Keeps track of which value/color item is being edited.  For performance reasons, only one item 
  // can be edited at a time.
  const [itemBeingEdited, setItemBeingEdited] = useState<ILayerStyleType_ClassifiedItem|undefined>(undefined);







  //-------------------------------------------------------------------------------
  // Enter EDIT mode for a specific class item.
  //-------------------------------------------------------------------------------
  function OnEnterEditMode(classifiedItem: ILayerStyleType_ClassifiedItem)
  {
    setItemBeingEdited(classifiedItem);
  }

  //-------------------------------------------------------------------------------
  // Exit EDIT mode.
  //-------------------------------------------------------------------------------
  function OnExitEditMode()
  {
    setItemBeingEdited(undefined);
  }

  //-------------------------------------------------------------------------------
  // A new gradient preset was selected.
  //-------------------------------------------------------------------------------
  async function OnPresetChanged(gradient_preset: ILayerStyleType_Gradient | undefined)
  {
    if(!props.rule.geometry || !props.rule.geometry.classified) return;

    setItemBeingEdited(undefined);

    let new_classified: ILayerStyleType_Classified | undefined = undefined;

    if(gradient_preset && gradient_preset.id !== GRADIENT_PRESET_EMPTY_ID)  // Colors based on gradient preset
      new_classified = await LayerStyle_CreateClassifiedRanges_GradientPreset(props.rule.geometry, props.rule.geometry.classified.items.length, gradient_preset);
    else // No colors assigned
      new_classified = await LayerStyle_CreateClassifiedRanges_NoColors(props.rule.geometry, props.rule.geometry.classified.items.length);

    if(!new_classified)
      return;

    // Notify caller of the change
    props.OnRuleClassifiedUpdated(new_classified);
  }

  //-------------------------------------------------------------------------------
  // The number of classes has changed.
  //-------------------------------------------------------------------------------
  const OnClassCountChanged = async (event: SelectChangeEvent<unknown>, child: ReactNode) => 
  {
    if(!props.rule.geometry || !props.rule.geometry.classified) return;

    setItemBeingEdited(undefined);

    const newClassCount: number = Number.parseInt(event.target.value as string);
    if(isNaN(newClassCount) || newClassCount < MIN_CLASSES || newClassCount > MAX_CLASSES)
    {
      Debug.error('OnClassCountChanged> Value is invalid or out of bounds');
      return;
    }

    let new_classified: ILayerStyleType_Classified | undefined = undefined;
    if(props.rule.geometry.classified.preset_id && props.rule.geometry.classified.preset_id !== GRADIENT_PRESET_EMPTY_ID)
    {
      // Colors are based on the current gradient preset
      const gradient_preset: ILayerStyleType_Gradient | undefined = GetGradientPreset(props.rule.geometry.classified.preset_id);
      new_classified = await LayerStyle_CreateClassifiedRanges_GradientPreset(props.rule.geometry, newClassCount, gradient_preset);
    }
    else // No colors set
      new_classified = await LayerStyle_CreateClassifiedRanges_NoColors(props.rule.geometry, newClassCount);

    if(!new_classified)
      return;

    // Notify caller of the change
    props.OnRuleClassifiedUpdated(new_classified);
  }

  //-------------------------------------------------------------------------------
  // A classified item color has changed.
  //-------------------------------------------------------------------------------
  function OnClassifiedColorChanged(value: string, colors: MuiColorInputColors, 
                                    classifiedItem: ILayerStyleType_ClassifiedItem)
  {
    if(!props.rule.geometry || !props.rule.geometry.classified)
      return;

    const updated_item: ILayerStyleType_ClassifiedItem = 
    {
      ...classifiedItem,
      color: value
    }

    // Replace it in the array
    const new_classified_items: ILayerStyleType_ClassifiedItem[] = props.rule.geometry.classified.items.map(oldItem => oldItem.id === classifiedItem.id ? updated_item : oldItem);

    const updated_classified: ILayerStyleType_Classified = 
    {
      ...props.rule.geometry?.classified,
      preset_id: GRADIENT_PRESET_EMPTY_ID,
      items: new_classified_items,
    }

    // Notify caller of the change
    props.OnRuleClassifiedUpdated(updated_classified);
  }

  //-------------------------------------------------------------------------------
  // An item's MIN VALUE value has changed.
  //-------------------------------------------------------------------------------
  function OnMinValueTextFieldChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, 
                                      classifiedItem: ILayerStyleType_ClassifiedItem,
                                      itemIndex: number): void 
  {
    if(!props.rule.geometry || !props.rule.geometry.classified) return;

    if(itemIndex === 0) return; // We don't allow editing the first item's MIN value

    const newValueStr: string = event.target.value as string;
    const newValueNum: number = Number.parseFloat(newValueStr);
    
    // Create an updated item

    const updated_item: ILayerStyleType_ClassifiedItem = 
    {
      ...classifiedItem,
      value_min: newValueNum,
    }

    let updated_classified_items: ILayerStyleType_ClassifiedItem[] = props.rule.geometry.classified.items.map(oldItem => oldItem.id === classifiedItem.id ? updated_item : oldItem);

    // Ranges must be contiguous (no gaps), so we must also update the MAX value of the item 
    // immediately ABOVE this one.

    const updated_item_above: ILayerStyleType_ClassifiedItem = 
    {
      ...props.rule.geometry.classified.items[itemIndex-1],
      value_max: newValueNum,
    }
    updated_classified_items = updated_classified_items.map(oldItem => oldItem.id === updated_item_above.id ? updated_item_above : oldItem);

    const updated_classified: ILayerStyleType_Classified = 
    {
      ...props.rule.geometry.classified,
      //items: props.rule.geometry.classified.items.map(oldItem => oldItem.id === classifiedItem.id ? updated_item : oldItem),
      items: updated_classified_items,
      preset_id: GRADIENT_PRESET_EMPTY_ID,
    }

    // Notify caller of the change
    props.OnRuleClassifiedUpdated(updated_classified);
  }

  //-------------------------------------------------------------------------------
  // An item's MAX VALUE value has changed.
  //-------------------------------------------------------------------------------
  function OnMaxValueTextFieldChanged(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, 
                                      classifiedItem: ILayerStyleType_ClassifiedItem,
                                      itemIndex: number): void 
  {
    if(!props.rule.geometry || !props.rule.geometry.classified) return;

    if(itemIndex === props.rule.geometry.classified.items.length-1) return; // We don't allow editing the last item's MAX value

    const newValueStr: string = event.target.value as string;
    const newValueNum: number = Number.parseFloat(newValueStr);

    // Create an updated item

    const updated_item: ILayerStyleType_ClassifiedItem = 
    {
      ...classifiedItem,
      value_max: newValueNum,
    }

    let updated_classified_items: ILayerStyleType_ClassifiedItem[] = props.rule.geometry.classified.items.map(oldItem => oldItem.id === classifiedItem.id ? updated_item : oldItem);

    // Ranges must be contiguous (no gaps), so we must also update the MIN value of the item 
    // immediately BELOW this one.

    const updated_item_below: ILayerStyleType_ClassifiedItem = 
    {
      ...props.rule.geometry.classified.items[itemIndex+1],
      value_min: newValueNum,
    }
    updated_classified_items = updated_classified_items.map(oldItem => oldItem.id === updated_item_below.id ? updated_item_below : oldItem);

    const updated_classified: ILayerStyleType_Classified = 
    {
      ...props.rule.geometry.classified,
      //items: props.rule.geometry.classified.items.map(oldItem => oldItem.id === classifiedItem.id ? updated_item : oldItem),
      items: updated_classified_items,
      preset_id: GRADIENT_PRESET_EMPTY_ID,
    }

    // Notify caller of the change
    props.OnRuleClassifiedUpdated(updated_classified);
  }
/*
  //-------------------------------------------------------------------------------
  // Manually add a new class item.
  //-------------------------------------------------------------------------------
  async function OnAddNewClass()
  {
    if(!props.rule.geometry || !props.rule.geometry.classified) return;

    const next_id = props.rule.geometry.classified.items.length === 0 ? 1 : props.rule.geometry.classified.items.reduce((prev, current) => (prev.id > current.id) ? prev : current).id + 1;

    const new_item: ILayerStyleType_ClassifiedItem = 
    {
      id: next_id,
      value_min: 0,
      value_max: 0,
      color: ''
    }

    const new_classified: ILayerStyleType_Classified = 
    {
      ...props.rule.geometry.classified,
      items: [...props.rule.geometry.classified.items, new_item ],
      preset_id: GRADIENT_PRESET_EMPTY_ID,
      auto_generated_values_interval: undefined,
    }

    // Put this new item into EDIT mode
    setItemBeingEdited(new_item);

    // Notify caller of the change
    props.OnRuleClassifiedUpdated(new_classified);
  }
*/

  //-------------------------------------------------------------------------------
  // Remove class item button was pushed.
  //-------------------------------------------------------------------------------
  function OnRemoveClass(classifiedItem: ILayerStyleType_ClassifiedItem)
  {
    if(!props.rule.geometry || !props.rule.geometry.classified) return;

    // Create an updated item

    const updated_classified: ILayerStyleType_Classified = 
    {
      ...props.rule.geometry.classified,
      items: props.rule.geometry.classified.items.filter(oldItem => oldItem.id !== classifiedItem.id),
      preset_id: GRADIENT_PRESET_EMPTY_ID,
      auto_generated_values_interval: undefined,
    }

    // Notify caller of the change
    props.OnRuleClassifiedUpdated(updated_classified);
  }
/*
  //-------------------------------------------------------------------------------
  // Clear All button was pushed.
  //-------------------------------------------------------------------------------
  async function OnClearAllClasses()
  {
    if(!props.rule.geometry || !props.rule.geometry.attribute_id) return;

    setItemBeingEdited(undefined);

    const updated_classified: ILayerStyleType_Classified = 
    {
      items: [],
      preset_id: GRADIENT_PRESET_EMPTY_ID,
      auto_generated_values_interval: undefined,
    }

    // Notify caller of the change
    props.OnRuleClassifiedUpdated(updated_classified);
  }
*/
  //-------------------------------------------------------------------------------
  // Assigns random colors to all current classes.
  // The values are unchanged.
  //-------------------------------------------------------------------------------
  async function OnAssignRandomColors()
  {
    if(!props.rule.geometry || !props.rule.geometry.classified || props.rule.geometry.classified.items.length === 0) return;

    setItemBeingEdited(undefined);

    // If we are generating random colors, we want them to be visually distict, so
    // the colors are all generated at once using a special algorithm.
    //
    // See:  https://www.npmjs.com/package/iwanthue

    const randomColors: string[] = iwanthue(props.rule.geometry.classified.items.length);

    // Rebuild the color items, but using the new random colors.

    const new_classified_items: ILayerStyleType_ClassifiedItem[] = [];
    for(let i=0; i < props.rule.geometry.classified.items.length; i++)
      new_classified_items.push(
        { 
          ...props.rule.geometry.classified.items[i],
          color: randomColors[i]
        });

      const new_classified: ILayerStyleType_Classified = 
      {
        ...props.rule.geometry.classified,
        items: new_classified_items,
        preset_id: GRADIENT_PRESET_EMPTY_ID,
      }

    // Notify caller of the change
    props.OnRuleClassifiedUpdated(new_classified);
  }

  //-------------------------------------------------------------------------------
  // Reverses the current list of colors.
  // The values are unchanged.
  //-------------------------------------------------------------------------------
  function OnReverseColors()
  {
    if(!props.rule.geometry || !props.rule.geometry.classified || !props.rule.geometry.classified.items ||
       props.rule.geometry.classified.items.length === 0) 
     return;

    // Create a new array of all the colors, but in reverse order

    const reversedColors: string[] = [];
    for(let i=props.rule.geometry.classified.items.length-1; i >= 0; i--)
      reversedColors.push(props.rule.geometry.classified.items[i].color);

    // Rebuild the color items, but using the new reversed colors

    const new_classified_items: ILayerStyleType_ClassifiedItem[] = [];
    for(let i=0; i < props.rule.geometry.classified.items.length; i++)
      new_classified_items.push(
        { 
          ...props.rule.geometry.classified.items[i],
          color: reversedColors[i]
        });

    // Create an update 'classified' item

    const new_classified: ILayerStyleType_Classified = 
    {
      ...props.rule.geometry.classified,
      items: new_classified_items
    }

    // Notify caller of the change
    props.OnRuleClassifiedUpdated(new_classified);
  }

  //-------------------------------------------------------------------------------
  // Auto-refresh all classes (both values and colors).
  //-------------------------------------------------------------------------------
  async function OnAutoRefreshAllClasses()
  {
    if(!props.rule.geometry || !props.rule.geometry.classified || props.rule.geometry.classified.items.length === 0) return;

    setItemBeingEdited(undefined);

    let new_classified: ILayerStyleType_Classified | undefined = undefined;

    if(props.rule.geometry.classified.preset_id !== GRADIENT_PRESET_EMPTY_ID)  // Colors based on gradient preset
    {
      // Colors are based on the current gradient preset
      const gradient_preset: ILayerStyleType_Gradient | undefined = GetGradientPreset(props.rule.geometry.classified.preset_id);
      new_classified = await LayerStyle_CreateClassifiedRanges_GradientPreset(props.rule.geometry, props.rule.geometry.classified.items.length, gradient_preset);
    }
    else // No colors assigned
      new_classified = await LayerStyle_CreateClassifiedRanges_NoColors(props.rule.geometry, props.rule.geometry.classified.items.length);

    if(!new_classified)
      return;

    // Notify caller of the change
    props.OnRuleClassifiedUpdated(new_classified);
  }

  //-------------------------------------------------------------------------------
  // Returns TRUE if the specified item is either first or last.
  //-------------------------------------------------------------------------------
  function IsFirstOrLastItem(itemIndex: number): boolean
  {
    if (itemIndex===0) 
      return true;

    if(!props.rule.geometry || !props.rule.geometry.classified) 
      return false;

    return itemIndex===props.rule.geometry.classified.items.length-1;
  }

  //-------------------------------------------------------------------------------
  // Expand this rule into multiple rules (one rule per item).
  //-------------------------------------------------------------------------------
  async function OnExpandRule()
  {
    props.OnExpandRule(props.rule);
  }










  

  if(!props.rule.geometry || !props.rule.geometry.classified || !props.rule.geometry.attribute_id) return null;


  // Main render

  return (

    <Stack sx={{ mx: 0, mt: 2 }}>

      <Typography sx={{ mt: 0, mb: 2, color: theme_textColorMain+'70', fontSize: '0.7rem' }}>
        There are two ways to set up the classes - automatically or manually.  To generate classes automatically, set the Class Count and select one of the gradient presets.  For the manual option, use the 'Add a New Class' button and enter in the value ranges and colors for each class.
      </Typography>

      <Stack direction='row' sx={{ mb: 2, alignItems: 'center', justifyContent: 'left' }}>

        {/* Select number of classes */}

        <Stack direction='column' sx={{ width: '140px' }}>

          <Typography sx={{ color: theme_textColorBlended, opacity: 0.8, fontSize: '0.8rem' }}>
            Class Count
          </Typography>

          <CustomSelect variant='standard' size='small'
                        value={props.rule.geometry.classified.items.length}
                        disabled={!props.rule.geometry.classified || !props.rule.geometry.attribute_id}
                        onChange={OnClassCountChanged}
                        sx={{ p: 0.5 }}>

            {Array.from({ length: MAX_CLASSES - MIN_CLASSES + 1 }, (_, i) => MIN_CLASSES + i).map(num =>
              <MenuItem key={num} value={num} sx={{ color: theme_textColorBlended, fontSize: '1.0rem' }}>{num}</MenuItem>
            )}

          </CustomSelect>
        </Stack>

        {/* Gradient presets */}

        <Stack sx={{ ml: 5, width: '100%' }}>
          <ColorGradientPresets allowEmptySelection={true}
                                selectedPresetID={props.rule.geometry.classified.preset_id}
                                OnPresetChanged={OnPresetChanged} />
        </Stack>

      </Stack>

      {/* Utility buttons */}

      <Stack direction='row' sx={{ my: 1, alignItems: 'center'}}>

        {/* <Button variant='outlined' sx={{ ml: 0, textTransform: 'none' }} 
                onClick={(_)=>OnClearAllClasses()}>
          <Stack direction='column'>
            <Typography sx={{ fontSize: '0.8rem', color: theme_textColorBlended }}>
              Clear All
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: theme_textColorMain, opacity: 0.5 }}>
              Remove all items
            </Typography>
          </Stack>
        </Button> */}

        <Button variant='outlined' sx={{ ml: 0, textTransform: 'none' }}
                onClick={(_)=>OnAutoRefreshAllClasses()}>
          <Stack direction='column'>
            <Typography sx={{ fontSize: '0.8rem', color: theme_textColorBlended }}>
              Auto-Refresh
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: theme_textColorMain, opacity: 0.5 }}>
              Values/colors regenerated
            </Typography>
          </Stack>
        </Button>


        <Button variant='outlined' sx={{ ml: 2, textTransform: 'none' }}
                onClick={(_)=>OnAssignRandomColors()}>
          <Stack direction='column'>
            <Typography sx={{ fontSize: '0.8rem', color: theme_textColorBlended }}>
              Random Colors
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: theme_textColorMain, opacity: 0.5 }}>
              Values unchanged
            </Typography>
          </Stack>
        </Button>

        <Button variant='outlined' sx={{ ml: 2, textTransform: 'none' }}
                onClick={(_)=>OnReverseColors()}>
          <Stack direction='column'>
            <Typography sx={{ fontSize: '0.8rem', color: theme_textColorBlended }}>
              Reverse Colors
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: theme_textColorMain, opacity: 0.5 }}>
              Values unchanged
            </Typography>
          </Stack>
        </Button>

      </Stack>

      {/* Stats */}

      <Stack direction='row' sx={{ mt: 2, mb: 0.5, alignItems: 'center' }}>

        {/* Show range (min/max) */}

        <Stack direction='row' sx={{ alignItems: 'center' }}>
          <Typography sx={{ fontSize: '1rem', color: theme_textColorBlended }}>
            Range:
          </Typography>
          <Typography sx={{ ml: 0.8, fontSize: '1rem', color: theme_limeGreen, opacity: 0.7, fontWeight: 'bold' }}>
            {FriendlyNumber(props.rule.geometry.classified.items[0].value_min,3)}
          </Typography>
          <Typography sx={{ mx: 1, fontSize: '1rem', color: theme_textColorBlended }}>
            to
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: theme_limeGreen, opacity: 0.7, fontWeight: 'bold' }}>
            {FriendlyNumber(props.rule.geometry.classified.items[props.rule.geometry.classified.items.length-1].value_max,3)}
          </Typography>
        </Stack>

        {/* If the value ranges were auto-generated (no manual editing), show the range interval (and units) */}

        {props.rule.geometry.classified.auto_generated_values_interval
          ?
            <Stack direction='row' sx={{ ml: 5, alignItems: 'center' }}>
              <Typography sx={{ fontSize: '1rem', color: theme_textColorBlended }}>
                Auto-Interval:
              </Typography>
              <Typography sx={{ ml: 0.8, fontSize: '1rem', color: theme_limeGreen, opacity: 0.7, fontWeight: 'bold' }}>
                {FriendlyNumber(props.rule.geometry.classified.auto_generated_values_interval,3)}
                {/* {FriendlyNumber(props.rule.geometry.classified.items.length >= 2 ? props.rule.geometry.classified.items[1].value_min - props.rule.geometry.classified.items[0].value_min : maxValue-minValue, 3)} */}
              </Typography>
              <Typography sx={{ ml: 0.6, fontSize: '1rem', color: theme_textColorBlended }}>
                {GetVectorLayerAttributeByID(props.layer, props.rule.geometry.attribute_id)?.unit}
              </Typography>
            </Stack>
          :null
        }

      </Stack>

      <Typography sx={{ mb: 1, fontSize: '0.7rem', color: theme_textColorMain+'70' }}>
        These ranges must be continuous and cover the entire range of the attribute's value.  The min and max values cannot be changed.  If you need to restrict any values please use the <i>Attribute Filter</i> feature above.
      </Typography>

      {/* All classes and their colors */}

      {props.rule.geometry.classified.items.map(function(classifiedItem,index)
      {
        if(itemBeingEdited?.id === classifiedItem.id)
        return (

          // EDIT MODE

          <Stack key={classifiedItem.id} direction='row' 
                 sx={{ my: 0.5, mx: '-10px', width: 'calc(100% + 10px)', alignItems: 'center', bgcolor: theme_textColorBlended+'30', 
                       borderRadius: 2, py: 1, pl: 1, justifyContent: 'space-between' }}>

            <Stack direction='row' sx={{ alignItems: 'center', width: '100%', justifyContent: 'start' }}>

              {/* Min Value edit box */}

              <Stack sx={{ width: '120px' }}>
                <Typography sx={{ color: theme_textColorBlended, fontSize: '0.8rem' }}>
                  Min Value
                </Typography>

                <CustomTextField variant='standard' size='small' autoComplete='off'
                                 disabled={index===0}
                                 value={classifiedItem.value_min}
                                 inputProps={{ type: 'number' }} 
                                 onChange={(e)=>OnMinValueTextFieldChanged(e,classifiedItem,index)}
                                 sx={{ p: 0 }}/>
              </Stack>

              {/* Max Value edit box */}

              <Stack sx={{ width: '120px', ml: 2 }}>
                <Typography sx={{ color: theme_textColorBlended, fontSize: '0.8rem' }}>
                  Max Value
                </Typography>

                <CustomTextField variant='standard' size='small' autoComplete='off'
                                 disabled={props.rule.geometry && props.rule.geometry.classified && index === props.rule.geometry.classified.items.length-1}
                                 value={classifiedItem.value_max}
                                 inputProps={{ type: 'number' }} 
                                 onChange={(e)=>OnMaxValueTextFieldChanged(e,classifiedItem,index)}
                                 sx={{ p: 0 }}/>
              </Stack>

            </Stack>

            <Stack direction='row' sx={{ alignItems: 'center' }}>

              {/* Color editor */}

              <Stack sx={{ ml: 2 }}>
                <Typography sx={{ color: theme_textColorBlended, fontSize: '0.8rem' }}>
                  Color
                </Typography>
                <MuiColorInputStyled variant='standard' size='small' format='hex' isAlphaHidden={true}
                                    //disabled={props.rule.geometry?.classified === undefined}
                                    value={classifiedItem.color}
                                    onChange={(v,c)=>OnClassifiedColorChanged(v,c,classifiedItem)}
                                    sx={{ width: '120px' }}/>
              </Stack>

              {/* Delete button */}

              <IconButton sx={{p: 0.3, ml: 1, opacity: IsFirstOrLastItem(index) ? 0 : 1 }} 
                          onClick={(_)=>OnRemoveClass(classifiedItem)}
                          disabled={IsFirstOrLastItem(index)}>
                <DeleteIcon sx={{ color: theme_textColorBlended, opacity: 0.7, p: 0.4 }}/>
              </IconButton>

              {/* Edit button (in EDIT mode) */}

              <IconButton sx={{p: 0.3, ml: 1}} onClick={(_)=>OnExitEditMode()}>
                <EditIcon sx={{ color: theme_orange, opacity: 0.7, p: 0.4 }}/>
              </IconButton>

            </Stack>

          </Stack>                    
        )

        else // VIEW MODE
        return (
  
            <Stack key={classifiedItem.id} direction='row' sx={{ my: 0, width: '100%', alignItems: 'center' }}>
              
              <Stack direction='row' sx={{ alignItems: 'center', width: '100%', justifyContent: 'start' }}>

                <Typography sx={{ width: '120px', fontSize: '0.9rem', color: theme_textColorMain+'A0' }}>
                  {parseFloat(classifiedItem.value_min.toFixed(2))}
                </Typography>
                <Typography sx={{ ml: 2, width: '120px', fontSize: '0.9rem', color: theme_textColorMain+'A0' }}>
                  {parseFloat(classifiedItem.value_max.toFixed(2))}
                </Typography>

              </Stack>

              <Stack direction='row' sx={{ mr: '50px', width: '150px', alignItems: 'center' }}>
                <Box sx={{ bgcolor: classifiedItem.color, width: '20px', height: '20px', borderRadius: 1, border: 1, borderColor: 'black' }}/>
                {classifiedItem.color && classifiedItem.color !== '' 
                  ? 
                    <Typography sx={{ ml: 1, color: theme_textColorMain+'A0' }}>
                      {classifiedItem.color}
                    </Typography>
                  :
                    <Typography sx={{ ml: 1, fontSize: '0.8rem', color: theme_textColorBlended+'90' }}>
                      No color set
                    </Typography>
                }
              </Stack>

              {/* Edit button (in VIEW mode) */}

              <IconButton sx={{p: 0.3, ml: 1 }} onClick={(_)=>OnEnterEditMode(classifiedItem)}>
                <EditIcon sx={{ color: theme_textColorBlended, opacity: 0.7, p: 0.4 }}/>
              </IconButton>

            </Stack>
        )
      })}

      <Stack direction='row' sx={{ mt: 2, alignItems: 'center', justifyContent: 'end', width: '100%' }}>

        {/* Button to add a new class item (manually) */}

        {/* <Stack sx={{ mt: 1, alignItems: 'start', width: '100%' }}>
          <Button variant='outlined' onClick={()=>OnAddNewClass()}
                  sx={{ textTransform: 'none', borderRadius: 2, px: 1, py: 0.2, fontSize: '0.7rem', opacity: 0.8 }}>
            Add a New Class
          </Button>      
        </Stack> */}

        {/* Button to expand this rule into multiple separate rules */}

        {props.rule.geometry.classified.items.length >= 2
          ?
            <Button variant='outlined' onClick={()=>OnExpandRule()}
                    sx={{ textTransform: 'none', borderRadius: 2, px: 1, py: 0.2, fontSize: '0.7rem', opacity: 0.75 }}>
              <Stack direction='column' sx={{ alignItems: 'start', maxWidth: '220px' }}>
                <Typography sx={{ fontSize: '0.75rem', color: theme_textColorBlended }}>
                  Expand this rule into {props.rule.geometry.classified.items.length} separate rules
                </Typography>
                <Typography sx={{ fontSize: '0.6rem', color: theme_textColorMain+'85', textAlign: 'left' }}>
                  One rule is created for each item.  All settings from this rule are copied over into the new rules.
                </Typography>
              </Stack>
            </Button>
          :null
        }

      </Stack>


    </Stack>
  )
}





//-------------------------------------------------------------------------------
// Get default color scheme: CLASSIFIED
//-------------------------------------------------------------------------------
export function GetDefaultLayerStyle_Classified(): ILayerStyleType_Classified
{
  const defaultItem: ILayerStyleType_Classified =
  {
    items: [],
    preset_id: GRADIENT_PRESET_EMPTY_ID,
    auto_generated_values_interval: undefined,
  }

  return defaultItem;
}

//-------------------------------------------------------------------------------
// Get min/max values for a layer's attribute.
//-------------------------------------------------------------------------------
export async function GetLayerAttribMinMax(attribute_id: number | undefined): Promise<{min_value: number, max_value: number} | undefined>
{
  if(!attribute_id)
  {
    Debug.error('GetLayerAttribMinMax> ERROR: undefined or invalid attribute ID');
    return undefined;
  }

  // Call the server

  const server = new CallServer();
  server.Add("attribute_id", attribute_id);
  server.Add("mode", "min_max");

  //useStore.getState().store_setDeletingLayerLibraryGroup(true);  // Tell the UI we are doing work

  const result = await server.Call('get', '/attribute_info');

  //useStore.getState().store_setDeletingLayerLibraryGroup(false); // Tell the UI we are no longer doing work

  if(result.success)
  {
    Debug.log('GetLayerAttribMinMax> API server call SUCCESS');
    //Debug.log('GetLayerAttribMinMax> SUCCESS! data=' + JSON.stringify(result.data));
    
    const min: number = result.data.min;
    if(min === undefined || min === null)
    {
      Debug.error(`GetLayerAttribMinMax> Invalid MIN value`);
      return undefined;
    }

    const max: number = result.data.max;
    if(max === undefined || max === null)
    {
      Debug.error(`GetLayerAttribMinMax> Invalid MAX value`);
      return undefined;
    }

    if(min > max)
    {
      Debug.error(`GetLayerAttribMinMax> Invalid MIN/MAX values`);
      return undefined;
    }

    //Debug.log(`GetLayerAttribMinMax> min_value=${values.length}.`);
    return { min_value: min, max_value: max };  // success
  }
  else
  {
    // Failure
    //ToastNotification('error', "Unable to get attribute min/max")
    Debug.error(`GetLayerAttribMinMax> ${result.errorCode} - ${result.errorMessage}`);
    return undefined;
  }
}

//-------------------------------------------------------------------------------
// Auto-generates value ranges for the specified 'classified' color scheme.
//-------------------------------------------------------------------------------
export async function LayerStyle_CreateClassifiedRanges_GenerateRanges(
                                       styleGeom: ILayerStyleRule_Geometry, 
                                       classCount: number): Promise<{ min: number; max: number; }[] | undefined>
{
  if(!styleGeom || !styleGeom.classified || classCount === undefined || (classCount < MIN_CLASSES || classCount > MAX_CLASSES))
    return undefined;

  // Get min and max values

  const minMaxValues: { min_value: number, max_value: number } | undefined = await GetLayerAttribMinMax(styleGeom.attribute_id);
  if(minMaxValues === undefined)
  {
    ToastNotification('error', 'Unable to get min/max values for this attribute');
    return undefined;
  }

  const minValue: number = minMaxValues.min_value;
  const maxValue: number = minMaxValues.max_value;

  // Break the min/max range into sub-ranges based on the class count

  return GetClassifiedRanges(minValue, maxValue, classCount);
}

//-------------------------------------------------------------------------------
// Creates ranges for the specified classified color scheme.
// Colors are left blank/undefined.
//-------------------------------------------------------------------------------
export async function LayerStyle_CreateClassifiedRanges_NoColors(
                                       styleGeom: ILayerStyleRule_Geometry, 
                                       classCount: number): Promise<ILayerStyleType_Classified | undefined>
{
  // Generate value ranges
  const ranges: { min: number; max: number; }[] | undefined = await LayerStyle_CreateClassifiedRanges_GenerateRanges(styleGeom, classCount);
  if(!ranges)
    return undefined;

  const new_classified_items: ILayerStyleType_ClassifiedItem[] = [];
  let nextID = 1;
  
  for(let i=0; i < classCount; i++)
  {
    new_classified_items.push(
      { 
        id: nextID++, 
        value_min: ranges[i].min,
        value_max: ranges[i].max,
        color: ''
      });
  }

  const new_classified: ILayerStyleType_Classified =
  {
    items: new_classified_items,
    preset_id: GRADIENT_PRESET_EMPTY_ID,
    auto_generated_values_interval: ranges[0].max-ranges[0].min,
  }

  // Success
  return new_classified;
}

//-------------------------------------------------------------------------------
// Creates ranges for the specified classified color scheme.
// Assigns colors based on the specified gradient preset.
//-------------------------------------------------------------------------------
export async function LayerStyle_CreateClassifiedRanges_GradientPreset(
                                       styleGeom: ILayerStyleRule_Geometry, 
                                       classCount: number,
                                       gradient_preset: ILayerStyleType_Gradient | undefined): Promise<ILayerStyleType_Classified | undefined>
{
  if(!gradient_preset)
    return undefined;

  // Generate value ranges
  const ranges: { min: number; max: number; }[] | undefined = await LayerStyle_CreateClassifiedRanges_GenerateRanges(styleGeom, classCount);
  if(!ranges || ranges.length === 0)
    return undefined;

  const new_classified_items: ILayerStyleType_ClassifiedItem[] = [];
  let nextID = 1;
  
  const minValue: number = ranges[0].min;
  const maxValue: number = ranges[ranges.length-1].max;

  // When a color gradient is used, we advance the value by this much each time
  // NOTE: The MIN value gets assigned the first gradient stop, and the MAX value gets 
  //       assigned the last gradient stop, with the rest being filled in linearly.
  const colorGradientValueStep: number = (maxValue - minValue) / (classCount - 1);
  let colorGradientValue: number = minValue;

  for(let i=0; i < classCount; i++)
  {
    let colorStr: string = '';

    // Gradient presets use a percent scale (0-100), so we must rescale the value to a percentage of min/max
    // Formula:  (VALUE - min) / (max - min) * 100
    const colorGradientValueScaledAsPercent: number = (colorGradientValue-minValue) / (maxValue-minValue) * 100;

    // Now we can get the interpolated gradient color to use for this value
    const colorBasedOnGradientPreset: string | undefined = GetGradientColorForValue(colorGradientValueScaledAsPercent, gradient_preset);
    if(colorBasedOnGradientPreset)
      colorStr = colorBasedOnGradientPreset;

    colorGradientValue += colorGradientValueStep;

    new_classified_items.push(
      { 
        id: nextID++, 
        value_min: ranges[i].min,
        value_max: ranges[i].max,
        color: colorStr
      });
  }

  const new_classified: ILayerStyleType_Classified =
  {
    items: new_classified_items,
    preset_id: gradient_preset?.id,
    auto_generated_values_interval: ranges[0].max-ranges[0].min,
  }

  // Success
  return new_classified;
}

//-------------------------------------------------------------------------------
// Returns the interpolated color for the specified value and gradient color scheme.
//-------------------------------------------------------------------------------
export function GetGradientColorForValue(value: number, gradient: ILayerStyleType_Gradient): string | undefined
{
  if(value === undefined || gradient === undefined)
    return undefined;

  // Scan through the color stops until we find which 2 stops contain our value.
  //
  // NOTE!!!  The items in the gradient MUST be in ascending order by value.

  let stopMin: ILayerStyleType_GradientItem | undefined = undefined;
  let stopMax: ILayerStyleType_GradientItem | undefined = undefined;

  for(let i=0; i < gradient.items.length-1; i++)
    if(value >= gradient.items[i].value && value <= gradient.items[i+1].value)
    {
      stopMin = gradient.items[i];
      stopMax = gradient.items[i+1];
      break;
    }

  if(!stopMin || !stopMax)
    return undefined;

  const gradientPercent: number = (value - stopMin.value) / (stopMax.value - stopMin.value);

  const gradientColor: string | undefined = InterpolateColors(stopMin.color, stopMax.color, gradientPercent);
  if(gradientColor === undefined)
  {
    Debug.error('GetGradientColorForValue> Unable to interpolate colors');
    return undefined; // error
  }

  // Success
  return gradientColor;
}

//-------------------------------------------------------------------------------
// Breaks a numerical range into a specified number of sub-ranges.
// 
// NOTE:  Uses the BigNumber library in order to produce accurate ranges.
//-------------------------------------------------------------------------------
function GetClassifiedRanges(minValue: number, maxValue: number, classCount: number): { min: number, max: number}[] | undefined
{
  if(minValue === undefined  || maxValue === undefined || classCount === undefined || (classCount < MIN_CLASSES || classCount > MAX_CLASSES))
  {
    Debug.error('GetClassifiedRanges> Bad input');
    return undefined;
  }

  // NOTE:  Doing the math using the JS primitive 'number' often leads to weird
  //        inaccuracy in the ranges produced.  So instead we use the BigNumber
  //        external library to produce accurate number ranges.
  const minValue_big = new BigNumber(minValue);
  const maxValue_big = new BigNumber(maxValue);

  // The size of each range
  //const rangeValue: number = (maxValue - minValue) / classCount;
  const rangeValue_big: BigNumber = maxValue_big.minus(minValue_big).dividedBy(classCount);

  const ranges: { min: number, max: number}[] = [];

  for(let i=0; i < classCount; i++)
    ranges.push(
      { 
        min: minValue_big.plus(rangeValue_big.times(i)).toNumber(),
        max: minValue_big.plus(rangeValue_big.times(i+1)).toNumber(),
      });

  // Success
  return ranges;
}

//-------------------------------------------------------------------------------
// Validates the specified rule geometry CLASSIFIED color scheme.
//
// Works for both regular rules and the special global defaults rule.
// 
// Returns an error message if validation FAILS, or empty string if it succeeds.
//-------------------------------------------------------------------------------
export function ValidateLayerStyleRuleGeom_Classified(classified: ILayerStyleType_Classified | undefined, 
                                                      errMsgPrefix: string): string
{
  if(classified === undefined)
    return errMsgPrefix + 'Invalid color scheme';

  if(classified.items === undefined || classified.items.length === 0)
    return errMsgPrefix + 'No color entries defined';

  for(let i=0; i < classified.items.length; i++)
  {
    if(classified.items[i].value_min === undefined || classified.items[i].value_max === undefined)
      return errMsgPrefix + `entry ${i+1} has an undefined value`;
    if(classified.items[i].value_min > classified.items[i].value_max)
      return errMsgPrefix + `entry ${i+1} has a min value that exceeds the max`;
    if(classified.items[i].color === undefined || classified.items[i].color.length === 0)
      return errMsgPrefix + `entry ${i+1} has an undefined color`;
  }

  // Success
  return '';
}

//-------------------------------------------------------------------------------
// Convert the specified CLASSIFIED rule into multiple rules (one rule per item).
//-------------------------------------------------------------------------------
export function OnExpandRule_Classified(rule: ILayerStyleRule, nextRuleID: number): ILayerStyleRule[] | undefined
{
  if(!rule || !rule.geometry || !rule.geometry.classified)
  {
    ToastNotification('error','Invalid color scheme (classified)')
    return undefined;
  }

  const items: ILayerStyleType_ClassifiedItem[] = rule.geometry.classified.items;

  if(items.length === 0)
  {
    ToastNotification('error','This color scheme contains no items');
    return undefined;
  }

  if(items.length > RULE_EXPANSION_MAX_ITEMS)
  {
    ToastNotification('error',`Rule expansion is limited to a max of ${RULE_EXPANSION_MAX_ITEMS} items`);
    return undefined;
  }

  // Convert each color scheme item into a new Single-Color rule

  const new_rules: ILayerStyleRule[] = [];

  for(let i=0; i < items.length; i++)
  {
    // Create a new geometry (Single Color)

    const newRuleGeom: ILayerStyleRule_Geometry = 
    {
      ...rule.geometry,
      color_scheme: 'single color',
      attribute_id: undefined,
      attribute_type: undefined,
      single_color: 
      {
        color: items[i].color
      },
      unique_values: undefined,
      classified: undefined,
      gradient: undefined,
    }

    // Create a filter

    const newFilter: ILayerAttribExpression =
    {
      clauses: 
      [
        {
          id: 1,
          attribute_id: rule.geometry.attribute_id,
          type: rule.geometry.attribute_type,
          operator: 'is in range',
          value: items[i].value_min.toString(),
          value_max: items[i].value_max.toString(),
        }
      ],
      operator: 'and'
    }

    // Create a new rule for this item

    const newRule: ILayerStyleRule = 
    {
      ...rule,
      id: nextRuleID++,
      description: `Range ${items[i].value_min} to ${items[i].value_max}`,
      geometry: newRuleGeom,
      filter: newFilter,
      enabled: true,
    }
    
    // Add the new rule to the list
    new_rules.push(newRule);
  }

  // Success
  return new_rules;
}